import { DEFAULT_MODE } from '../theme/brands';

/**
 * Site-theme override stylesheet for embedded ActiveCampaign forms.
 *
 * The AC exports in public/forms/*.txt are generated from the live account
 * by scripts/sync-ac-forms.js (never hand-edited, see docs/ACTIVE_CAMPAIGN.md),
 * so each one ships AC's own <style> block styling the form as a white box in
 * whatever colours the form has in AC. This module generates a stylesheet
 * that re-themes the injected form to the live design tokens (--color-*), so
 * the forms follow the shipped brand palette automatically.
 *
 * Specificity strategy: AC scopes everything under the form's id
 * (e.g. `#_form_6_ input[type="text"]`), sprinkling `!important` on the
 * contested properties. Overrides here use the id twice (`#_form_6_#_form_6_`)
 * which out-specifies every AC selector, and mirror AC's `!important` only
 * where AC itself uses it. The generated <style> is appended after the AC
 * styles in the DOM, so equal-importance ties also resolve our way.
 *
 * @param {string} formId - The injected form's element id (e.g. `_form_6_`).
 * @returns {string} CSS text themed to the current design tokens.
 */
export function buildAcFormThemeCss(formId) {
  const id = CSS.escape(formId);
  // Doubled id: beats AC's single-id selectors at every property.
  const f = `#${id}#${id}`;

  return `
${f} {
  background: transparent !important;
  border: 0 !important;
  border-radius: 0 !important;
  margin: 0 auto;
  padding: 0;
  max-width: 560px;
  font-family: 'Manrope', 'Roboto', 'Helvetica', sans-serif;
  font-size: 1rem;
  line-height: 1.6;
  color: var(--color-text);
  color-scheme: ${DEFAULT_MODE === 'dark' ? 'dark' : 'light'};
}

/* ---- Headings & copy ---- */
${f} ._form-title {
  font-family: inherit;
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.3;
  letter-spacing: -0.01em;
  color: var(--color-heading);
  margin: 0 0 1.5rem;
  text-wrap: balance;
}
/* Pages that render their own heading above the embed set this class on the
   wrapper so the export's built-in title doesn't repeat it. */
.ac-embed--hide-title ${f} ._form-title {
  display: none;
}
${f} ._html-code,
${f} ._form-body {
  font-family: inherit;
  color: var(--color-text);
}
${f} ._html-code p {
  font-size: 0.95rem;
  line-height: 1.6;
  color: var(--color-text-muted);
}
${f} ._html-code :is(h1, h2, h3, h4, h5, h6) {
  font-family: inherit;
  color: var(--color-heading);
}

/* ---- Field scaffolding ---- */
${f} ._form_element {
  margin-bottom: 1.25rem;
}
${f} ._form-label,
${f} ._form_element ._form-label {
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.4;
  letter-spacing: 0.01em;
  color: var(--color-text);
  margin-bottom: 0.4rem;
}
${f} .field-required {
  color: color-mix(in srgb, var(--color-destructive) 45%, #ffffff);
}

/* ---- Inputs, textareas, selects ---- */
${f} input[type='text'],
${f} input[type='tel'],
${f} input[type='date'],
${f} input[type='time'],
${f} input[type='email'],
${f} input[type='number'],
${f} select,
${f} textarea:not(.g-recaptcha-response) {
  background: var(--color-input) !important;
  color: var(--color-text) !important;
  border: 1px solid var(--color-border) !important;
  border-radius: var(--radius-md) !important;
  padding: 12px 14px;
  font-family: inherit;
  font-size: 0.95rem;
  line-height: 1.5;
  transition:
    border-color 150ms ease,
    box-shadow 150ms ease;
}
${f} :is(input, select, textarea):hover:not(:focus):not(._has_error) {
  border-color: rgba(255, 255, 255, 0.24) !important;
}
${f} :is(input, select, textarea):focus {
  outline: none;
  border-color: var(--color-primary) !important;
  box-shadow: 0 0 0 3px
    color-mix(in srgb, var(--color-focus-ring) 25%, transparent);
}
${f} input::placeholder,
${f} textarea::placeholder {
  color: var(--color-text-muted);
  opacity: 1;
}
${f} input:-webkit-autofill {
  -webkit-box-shadow: 0 0 0 1000px var(--color-input) inset !important;
  -webkit-text-fill-color: var(--color-text) !important;
  caret-color: var(--color-text);
}
${f} textarea:not(.g-recaptcha-response) {
  min-height: 120px;
  resize: vertical !important;
}
${f} input[type='checkbox'],
${f} input[type='radio'] {
  accent-color: var(--color-primary);
}
${f} ._row span:not(.field-required),
${f} ._row label,
${f} ._form-checkbox-option-label,
${f} ._form-checkbox-option-label-with-description {
  font-family: inherit;
  color: var(--color-text);
}
${f} .sms_consent_checkbox .sms_consent_message {
  color: var(--color-text-muted);
}

/* Keep field text at 16px on small screens so iOS doesn't zoom on focus. */
@media (max-width: 667px) {
  ${f} :is(input, select, textarea):not(.g-recaptcha-response) {
    font-size: 1rem;
  }
}

/* ---- Submit button (matches the site's primary pill CTA) ---- */
${f} ._submit {
  width: 100%;
  margin-top: 0.5rem;
  padding: 13px 28px !important;
  font-family: inherit;
  font-size: 1rem !important;
  font-weight: 600;
  color: var(--color-primary-text) !important;
  background: var(--color-primary) !important;
  border: 0 !important;
  border-radius: var(--radius-full) !important;
  cursor: pointer;
  transition:
    background-color 200ms ease,
    transform 200ms ease,
    box-shadow 200ms ease;
}
${f} ._submit:hover:not(:disabled) {
  background: var(--color-primary-light) !important;
  transform: translateY(-1px);
  box-shadow: 0 8px 20px
    color-mix(in srgb, var(--color-primary) 25%, transparent);
}
${f} ._submit:active:not(:disabled) {
  transform: translateY(0);
  box-shadow: none;
}
${f} ._submit:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px
    color-mix(in srgb, var(--color-focus-ring) 40%, transparent);
}
${f} ._submit:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}
${f} ._submit.processing::before {
  background-image: linear-gradient(
      var(--color-primary),
      var(--color-primary)
    ),
    conic-gradient(var(--color-primary), var(--color-primary-text));
}

/* ---- Validation errors (AC's light-pink chips, re-cut for dark) ---- */
${f} ._error-inner {
  background-color: color-mix(
    in srgb,
    var(--color-destructive) 12%,
    var(--color-card)
  );
  background-image: none;
  border: 1px solid color-mix(in srgb, var(--color-destructive) 45%, transparent);
  border-radius: var(--radius-md);
  box-shadow: none;
  padding: 8px 12px;
  font-family: inherit;
  font-size: 0.85rem;
  font-weight: 500;
  line-height: 1.4;
  text-align: left;
  color: color-mix(in srgb, var(--color-destructive) 35%, #ffffff);
}
${f} ._error-arrow {
  display: none;
}
/* AC hardcodes the tooltip offset for its 35px inputs; anchor to the actual
   field height instead so the chip clears the taller themed inputs. */
${f} ._error._above {
  bottom: calc(100% + 4px);
  padding-bottom: 0;
}
${f} input._has_error,
${f} textarea._has_error {
  border-color: var(--color-destructive) !important;
}
${f} input[type='checkbox']._has_error {
  outline-color: var(--color-destructive);
}

/* ---- Phone input (intl-tel-input) ---- */
${f} .iti input {
  background: var(--color-input) !important;
  border: 1px solid var(--color-border) !important;
  border-radius: var(--radius-md) !important;
}
${f} .iti--separate-dial-code .iti__selected-flag {
  background-color: var(--color-muted);
  border-radius: var(--radius-md) 0 0 var(--radius-md);
}
${f} .iti--separate-dial-code .iti__selected-flag:hover {
  background-color: rgba(255, 255, 255, 0.08);
}
${f} .iti__country-list {
  background: var(--color-card);
  color: var(--color-text);
  border: 1px solid var(--color-border);
}
${f} .iti__country.iti__highlight {
  background: var(--color-muted);
}
${f} .iti__dial-code {
  color: var(--color-text-muted);
}

/* ---- reCAPTCHA (widget itself stays Google-light) ---- */
${f} .g-recaptcha {
  margin-top: 0.25rem;
}

/* ---- Post-submit + AC branding ---- */
${f} ._form-thank-you {
  font-family: inherit;
  font-size: 1.05rem;
  line-height: 1.6;
  color: var(--color-text);
}
${f} ._form-branding {
  color: var(--color-text-muted);
  opacity: 0.8;
}
${f} ._form-branding ._logo {
  /* AC's "_dark" variant ships a near-black logo; swap in the light one. */
  background-image: url('https://d226aj4ao1t61q.cloudfront.net/hh9ujqgv5_aclogo_li.png');
}

@media (prefers-reduced-motion: reduce) {
  ${f} ._submit,
  ${f} :is(input, select, textarea) {
    transition: none;
  }
  ${f} ._submit:hover:not(:disabled) {
    transform: none;
  }
}
`;
}
